import { useState, useEffect } from "react";
import Icon from "../../../components/ui/Icon";
import SignalBars from "../../../components/ui/SignalBars";
import { BRAND } from "../../../brand";
import { useAppState } from "../../../state/appState";
import {
  SCAN_DOT_INTERVAL_MS,
  GATEWAY_STAGGER_MS,
  GATEWAY_SCAN_DURATION_MS,
  CONNECT_ANIMATION_MS,
  DISCONNECT_ANIMATION_MS,
} from "../../../constants/app";
import { rssiStrength } from "../../../constants/gateways";
import { GatewayService } from "../../../services/gateway";

const GATEWAYS = GatewayService.getAll();

export default function ConnectGatewayPage({ onStartDiscovery, onDisconnect }) {
  const { actions, gatewayConnected, connectedGatewayId: persistedGatewayId } = useAppState();
  const isConnected = gatewayConnected;

  const [connectedGatewayId, setConnectedGatewayId] = useState(
    persistedGatewayId ?? (isConnected ? GATEWAYS[0].id : null)
  );
  const activeGateway = isConnected
    ? (GATEWAYS.find((g) => g.id === connectedGatewayId) ?? GATEWAYS[0])
    : null;

  const [scan, setScan] = useState(isConnected ? "found" : "idle");
  const [visibleCount, setVisibleCount] = useState(isConnected ? GATEWAYS.length : 0);
  const [selected, setSelected] = useState(isConnected ? (activeGateway ?? GATEWAYS[0]) : null);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [scanDots, setScanDots] = useState(0);
  const [showNetworkPrompt, setShowNetworkPrompt] = useState(false);

  useEffect(() => {
    if (isConnected) {
      setScan("found");
      setVisibleCount(GATEWAYS.length);
      setSelected((prev) => prev ?? GATEWAYS.find((g) => g.id === connectedGatewayId) ?? GATEWAYS[0]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected]);

  useEffect(() => {
    if (scan !== "scanning") return;
    const t = setInterval(() => setScanDots((d) => (d + 1) % 4), SCAN_DOT_INTERVAL_MS);
    return () => clearInterval(t);
  }, [scan]);

  useEffect(() => {
    if (scan !== "found") { setVisibleCount(0); return; }
    if (visibleCount >= GATEWAYS.length) return;
    let idx = visibleCount;
    const t = setInterval(() => {
      idx += 1;
      setVisibleCount(idx);
      if (idx >= GATEWAYS.length) clearInterval(t);
    }, GATEWAY_STAGGER_MS);
    return () => clearInterval(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scan]);

  const handleScan = () => {
    setScan("scanning");
    setSelected(activeGateway ?? null);
    setVisibleCount(0);
    setTimeout(() => setScan("found"), GATEWAY_SCAN_DURATION_MS);
  };

  const handleConnect = () => {
    if (!selected || selected.id === activeGateway?.id) return;
    setConnecting(true);
    setConnectedGatewayId(selected.id);
    setTimeout(() => {
      setConnecting(false);
      actions.connectGateway(selected.id, selected);
      setShowNetworkPrompt(true);
    }, CONNECT_ANIMATION_MS);
  };

  const handleDisconnect = () => {
    setDisconnecting(true);
    setShowNetworkPrompt(false);
    setTimeout(() => {
      setDisconnecting(false);
      if (onDisconnect) onDisconnect();
    }, DISCONNECT_ANIMATION_MS);
  };

  // ── Network scan prompt ───────────────────────────────────────────────────
  if (showNetworkPrompt) {
    const gw = GATEWAYS.find((g) => g.id === connectedGatewayId) ?? GATEWAYS[0];
    return (
      <div className="space-y-4">
        <div className="flex flex-col items-center gap-4 rounded-3xl border border-emerald-500/30 bg-emerald-500/5 px-4 py-8 text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full border border-emerald-500/30 bg-emerald-500/20 text-emerald-400">
            <Icon name="bluetooth_connected" className="text-3xl" />
          </span>
          <div className="space-y-1">
            <p className="text-base font-semibold text-brand-light">Gateway connected</p>
            <p className="text-sm text-brand-light/50">{gw.name}</p>
          </div>
        </div>
        <div className="rounded-3xl border border-brand-navy bg-brand-purple px-4 py-4 space-y-3">
          <p className="text-sm font-semibold text-brand-light">Scan for devices on the network?</p>
          <p className="text-xs text-brand-light/50">
            Discover all cBerry nodes currently active on the CAN bus. You can also trigger a scan
            later from the Network screen.
          </p>
          <div className="grid grid-cols-2 gap-2 pt-1">
            <button
              type="button"
              onClick={() => { setShowNetworkPrompt(false); if (onStartDiscovery) onStartDiscovery(); }}
              className="rounded-xl bg-brand-magenta px-3 py-3 text-sm font-semibold text-white shadow hover:bg-brand-pink transition"
            >
              Scan Network
            </button>
            <button
              type="button"
              onClick={() => setShowNetworkPrompt(false)}
              className="rounded-xl border border-brand-navy bg-brand-navy px-3 py-3 text-sm font-semibold text-brand-light hover:border-brand-magenta/50 transition"
            >
              Skip
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Scanning ──────────────────────────────────────────────────────────────
  if (scan === "scanning") {
    const dots = ".".repeat(scanDots);
    return (
      <div className="space-y-4">
        {isConnected && (
          <div className="flex items-center gap-2 px-1 text-[11px] text-brand-light/50">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
            <span>Connected to {activeGateway.name}</span>
          </div>
        )}
        <div className="flex flex-col items-center justify-center gap-6 py-8">
          <div className="relative flex items-center justify-center">
            {[1, 2, 3].map((ring) => (
              <span
                key={ring}
                className="absolute rounded-full border border-brand-magenta/30"
                style={{
                  width: `${ring * 52}px`,
                  height: `${ring * 52}px`,
                  animation: `ping ${1 + ring * 0.35}s cubic-bezier(0,0,0.2,1) infinite`,
                  animationDelay: `${ring * 0.18}s`,
                }}
              />
            ))}
            <span
              className="relative flex h-14 w-14 items-center justify-center rounded-full border-2 border-brand-magenta bg-brand-purple text-brand-magenta shadow-lg"
              style={{ boxShadow: `0 0 24px ${BRAND.magenta}55` }}
            >
              <Icon name="bluetooth_searching" className="text-3xl" />
            </span>
          </div>
          <div className="text-center space-y-1">
            <p className="text-base font-semibold text-brand-light">Scanning for gateways{dots}</p>
            <p className="text-xs text-brand-light/50">Looking for nearby cBerry Bluetooth-CAN bridges</p>
          </div>
          <div className="w-full rounded-2xl border border-brand-navy bg-brand-navy px-4 py-3 space-y-1.5 text-[11px] font-mono text-brand-light/50">
            <p className="text-brand-magenta/80">▶ Initialising BLE adapter…</p>
            {scanDots >= 1 && <p>▶ Setting scan parameters (RSSI &gt; −90 dBm)…</p>}
            {scanDots >= 2 && <p>▶ Broadcasting discovery UUID 0xCBE1…</p>}
            {scanDots >= 3 && <p className="text-amber-300/80">▶ Receiving advertisement packets…</p>}
          </div>
        </div>
      </div>
    );
  }

  // ── Found ─────────────────────────────────────────────────────────────────
  if (scan === "found") {
    const isActiveSelected = selected?.id === activeGateway?.id;
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between text-[11px] px-0.5">
          <span className="text-brand-light/70">
            {GATEWAYS.length} gateways found
            {isConnected && <> · <span className="text-emerald-400">1 connected</span></>}
          </span>
          <button type="button" onClick={handleScan} className="text-brand-magenta hover:text-brand-pink transition font-semibold">
            Rescan
          </button>
        </div>
        <div className="space-y-2">
          {GATEWAYS.slice(0, visibleCount).map((gw) => {
            const isSelected = selected?.id === gw.id;
            const isCurrent = isConnected && gw.id === activeGateway?.id;
            return (
              <button
                key={gw.id}
                type="button"
                onClick={() => setSelected(gw)}
                className={`w-full rounded-2xl border px-4 py-3 text-left flex items-center gap-3 transition ${
                  isCurrent
                    ? isSelected ? "border-emerald-500/60 bg-brand-purple shadow-[0_0_12px_rgba(52,211,153,0.12)]"
                                 : "border-emerald-500/30 bg-brand-purple hover:border-emerald-500/60"
                    : isSelected ? "border-brand-magenta bg-brand-purple shadow-[0_0_12px_rgba(187,22,163,0.25)]"
                                 : "border-brand-navy bg-brand-purple hover:border-brand-magenta/50"
                }`}
                style={{ animation: "fadeInUp 260ms ease both" }}
              >
                <span
                  className={`flex h-10 w-10 items-center justify-center rounded-full border ${
                    isCurrent ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-400"
                    : isSelected ? "border-brand-magenta bg-brand-magenta/15 text-brand-magenta"
                    : "border-brand-navy bg-brand-navy text-brand-light/60"
                  }`}
                >
                  <Icon name={isCurrent ? "bluetooth_connected" : "bluetooth"} className="text-xl" />
                </span>
                <div className="flex-1 flex flex-col min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-brand-light truncate">{gw.name}</span>
                    {isCurrent && (
                      <span className="shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                        active
                      </span>
                    )}
                  </div>
                  <span className="text-[11px] text-brand-light/50">
                    HW {gw.hw} · FW {gw.fw} · {gw.rssi} dBm
                  </span>
                </div>
                <SignalBars strength={rssiStrength(gw.rssi)} color="magenta" />
                {isSelected && !isCurrent && (
                  <Icon name="check_circle" className="text-brand-magenta text-xl" />
                )}
              </button>
            );
          })}
        </div>
        {visibleCount >= GATEWAYS.length && (
          <>
            {(!isConnected || !isActiveSelected) && (
              <button
                type="button"
                onClick={handleConnect}
                disabled={!selected || connecting || isActiveSelected}
                className={`w-full rounded-xl px-4 py-3 text-sm font-semibold shadow transition active:scale-[0.99] ${
                  selected && !connecting && !isActiveSelected
                    ? "bg-brand-magenta text-white hover:bg-brand-pink"
                    : "bg-brand-purple/40 text-brand-light/40 cursor-not-allowed"
                }`}
                style={{ animation: "fadeInUp 260ms 200ms ease both" }}
              >
                {connecting ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="material-symbols-outlined text-lg" style={{ animation: "spin 1s linear infinite" }}>sync</span>
                    Connecting…
                  </span>
                ) : selected && !isActiveSelected ? `Connect to ${selected.name}` : "Select a gateway"}
              </button>
            )}
            {isConnected && isActiveSelected && (
              <button
                type="button"
                onClick={handleDisconnect}
                disabled={disconnecting}
                className="w-full flex items-center justify-center gap-2 rounded-xl border border-red-500/40 bg-red-900/10 px-4 py-3 text-sm font-semibold text-red-400 hover:bg-red-900/20 transition active:scale-[0.99]"
                style={{ animation: "fadeInUp 260ms 200ms ease both" }}
              >
                {disconnecting ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="material-symbols-outlined text-lg" style={{ animation: "spin 1s linear infinite" }}>sync</span>
                    Disconnecting…
                  </span>
                ) : (
                  <>
                    <Icon name="bluetooth_disabled" className="text-lg" />
                    Disconnect from {activeGateway.name}
                  </>
                )}
              </button>
            )}
          </>
        )}
      </div>
    );
  }

  // ── Idle ──────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col items-center gap-4 rounded-3xl border border-brand-navy bg-brand-navy px-4 py-14 text-center">
      <span className="flex h-14 w-14 items-center justify-center rounded-full bg-brand-purple text-brand-light/60">
        <Icon name="bluetooth_searching" className="text-4xl" />
      </span>
      <div className="space-y-1">
        <p className="text-base font-semibold text-brand-light">No gateways found</p>
        <p className="text-sm text-brand-light/50">Scan for nearby cBerry Bluetooth-CAN bridges.</p>
      </div>
      <button
        type="button"
        onClick={handleScan}
        className="w-full max-w-xs rounded-xl bg-brand-magenta px-4 py-3 text-sm font-semibold text-white shadow hover:bg-brand-pink transition"
      >
        Scan for Gateways
      </button>
    </div>
  );
}
